//#region types
import { AgentReply, AgentTurn, isReadOnlyCard, isShownAnswer, outcomeOf } from "models/agent/agentContract";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { AgentConversationState } from "utils/helpers/agentConversationStore";
//#endregion

export type AgentMode = "ask" | "working" | "answered" | "clarify" | "review" | "sent";

/** Where a reply takes her: an answer to accept, a question to answer, or a case to look over before it goes to support. */
export type ReplyRoute = "answered" | "clarify" | "review";

export const routeOf = (reply: AgentReply): ReplyRoute => {
  switch (reply.kind) {
    case "answer": {
      if (isShownAnswer(reply)) return "answered";
      if (outcomeOf(reply) === "NeedsClarification") return "clarify";
      return "review";
    }
    case "question":
      return "clarify";
    case "card":
      return isReadOnlyCard(reply) ? "answered" : "review";
    case "receipt":
      return "answered";
    default:
      return "review";
  }
};

/**
 * Sends her message, once more on a fresh session when the service has forgotten hers (it keeps them in
 * memory). An answer to the agent's question means nothing to a new session, so that goes to review.
 */
export const sendRecovering = async (
  send: (freshSession: boolean) => Promise<AgentReply>,
  isSessionLost: (error: unknown) => boolean,
  answeringQuestion: boolean,
  onSessionLost: () => void
): Promise<AgentReply | "review"> => {
  try {
    return await send(false);
  } catch (error) {
    if (!isSessionLost(error)) throw error;
    onSessionLost();
    return answeringQuestion ? "review" : send(true);
  }
};

export const lastAgentTurn = (turns: AgentTurn[]): AgentTurn | undefined => {
  const last = turns.at(-1);
  return last?.who === "agent" && last.reply ? last : undefined;
};

type ModeState = Pick<AgentConversationState, "turns" | "pending" | "review" | "sentCase">;

export const modeOf = ({ turns, pending, review, sentCase }: ModeState): AgentMode => {
  if (sentCase) return "sent";
  if (pending?.kind === "send" && review) return "review";
  if (pending) return "working";
  if (review) return "review";
  if (!turns.some((turn) => turn.who === "customer")) return "ask";

  const reply = lastAgentTurn(turns)?.reply;
  const route = reply ? routeOf(reply) : undefined;
  if (route === "answered" || route === "clarify") return route;
  // No usable reply (a failed call, or a reload mid-request): support gets it, once she has looked.
  return "review";
};

export const pickedChoiceOf = (turns: AgentTurn[]): string | undefined => turns.findLast((turn) => turn.choice)?.choice;

export const questionAttachments = (
  turns: AgentTurn[],
  attachmentsOf: (turnId: number) => UploadedFile[]
): UploadedFile[] => turns.filter((turn) => turn.who === "customer").flatMap((turn) => attachmentsOf(turn.id));
